import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function AddCollection({
  open,
  onClose,
  setOpen,
}: ModalProps) {
  const [products, setProducts] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [collectionType, setCollectionType] = useState("Manual");
  const [surfaces, setSurfaces] = useState<string[]>([]);
  const [priority, setPriority] = useState("Normal");
  const [pinToTop, setPinToTop] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timezone, setTimezone] = useState("GST");

  return (
    <Modal className="modal rejectReason_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          {/* Header */}
          <div className="modal_title">
            <h2>Add Collection</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Form */}
          <form className="form">
            <div className="gap_p">
              {/* Collection Type - ADDED */}
              <div className="control_group">
                <FormLabel component="legend" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>Collection Type</FormLabel>
                <RadioGroup
                  row
                  value={collectionType}
                  onChange={(e) => setCollectionType(e.target.value)}
                >
                  <FormControlLabel value="Manual" control={<Radio />} label="Manual" />
                  <FormControlLabel value="Dynamic" control={<Radio />} label="Dynamic" />
                  <FormControlLabel value="Seasonal" control={<Radio />} label="Campaign / Seasonal" />
                </RadioGroup>
              </div>

              {/* Collection Name */}
              <div className="control_group">
                <label htmlFor="collection-name">Collection Name</label>
                <TextField
                  placeholder="Enter collection name"
                  fullWidth
                  hiddenLabel
                  inputProps={{ id: "collection-name" }}
                />
              </div>

              {/* Description */}
              <div className="control_group">
                <label htmlFor="collection-description">Description</label>
                <TextField
                  placeholder="Short description about this collection"
                  fullWidth
                  multiline
                  rows={2}
                  hiddenLabel
                  inputProps={{ id: "collection-description" }}
                />
              </div>

              {/* Population Logic - CONDITIONAL */}
              {collectionType === "Manual" ? (
                <div className="control_group">
                  <label htmlFor="add-products">Add Products</label>
                  <Select
                    fullWidth
                    multiple
                    value={products}
                    onChange={(e) => setProducts(e.target.value as string[])}
                    displayEmpty
                    inputProps={{ id: "add-products" }}
                  >
                    <MenuItem value="prod1">Men Sports Shoes</MenuItem>
                    <MenuItem value="prod2">Smart Watch</MenuItem>
                    <MenuItem value="prod3">Wireless Earbuds</MenuItem>
                  </Select>
                </div>
              ) : collectionType === "Dynamic" ? (
                <div className="control_group" style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                  <label style={{ marginBottom: '8px', display: 'block', fontWeight: 600 }}>Rule Builder Preview</label>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>
                    Current Rules:<br />
                    • Category is "Footwear"<br />
                    • Price &gt; 200 AED<br />
                    • Stock &gt; 0
                  </p>
                  <Button size="small" sx={{ textTransform: 'none', mt: 1 }}>Configure Rules</Button>
                </div>
              ) : null}

              {/* Surface / Placement - NEW */}
              <div className="control_group">
                <label htmlFor="surface-placement">Surface / Placement</label>
                <Select
                  fullWidth
                  multiple
                  value={surfaces}
                  onChange={(e) => setSurfaces(e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).join(', ')}
                  inputProps={{ id: "surface-placement" }}
                >
                  <MenuItem value="Homepage">Homepage</MenuItem>
                  <MenuItem value="Category page">Category page</MenuItem>
                  <MenuItem value="Search boost">Search boost</MenuItem>
                  <MenuItem value="App only">App only</MenuItem>
                  <MenuItem value="Web only">Web only</MenuItem>
                </Select>
              </div>

              {/* Scheduling - NEW */}
              <div className="control_group">
                <Accordion sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px !important' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Scheduling & Timezone</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                    <Select
                      fullWidth
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                    >
                      <MenuItem value="GST">GST (Gulf Standard Time)</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </Select>
                  </AccordionDetails>
                </Accordion>
              </div>

              {/* Priority & Ordering - NEW */}
              <div className="control_group">
                <FormLabel component="legend" sx={{ fontWeight: 600, color: '#333', mb: 1, fontSize: '14px' }}>Priority level</FormLabel>
                <RadioGroup
                  row
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <FormControlLabel value="Low" control={<Radio size="small" />} label="Low" />
                  <FormControlLabel value="Normal" control={<Radio size="small" />} label="Normal" />
                  <FormControlLabel value="High" control={<Radio size="small" />} label="High" />
                </RadioGroup>

                {surfaces.includes("Homepage") && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pinToTop}
                        onChange={(e) => setPinToTop(e.target.checked)}
                      />
                    }
                    label="Pin to top (Homepage)"
                    sx={{ mt: 1 }}
                  />
                )}
              </div>

              {/* Checkboxes */}
              <div className="control_group checkbox_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                    />
                  }
                  label="Active"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={featured}
                      onChange={(e) => setFeatured(e.target.checked)}
                    />
                  }
                  label="Featured"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="footer_btn_flex" style={{ flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <Button
                className="btn btn_primary"
                onClick={() => setOpen(false)}
                fullWidth
              >
                Create Collection
              </Button>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Will be visible on: {surfaces.length > 0 ? surfaces.join(', ') : 'None selected'}
              </Typography>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
